import React, { useState } from 'react';
import { NavLink } from 'react-router';
import { Menu, X } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import Button from '../ui/Button';
import { Sheet, SheetClose, SheetContent, SheetTitle, SheetTrigger } from '../pilot-ui/sheet';
import { useUnreadNoteCount } from '../../hooks/useUnreadNoteCount';

const linkClass = ({ isActive }: { isActive: boolean }) =>
  `block rounded-md px-2 py-1.5 text-sm no-underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sidebar-ring ${isActive
    ? 'bg-sidebar-accent font-semibold text-sidebar-accent-foreground'
    : 'text-sidebar-foreground hover:bg-sidebar-hover hover:text-sidebar-hover-foreground'}`;

/**
 * The console's links, signed-in email and Logout. Wide windows show them in
 * the sidebar; narrower ones open them from the Menu button, and following a
 * link there closes the menu (`onNavigate`).
 */
const ConsoleNavigation: React.FC<{ onNavigate?: () => void }> = ({ onNavigate }) => {
  const { logout, hasPermission, email, scopes } = useAuth();
  const { count } = useUnreadNoteCount();
  const showUnreadCount = hasPermission('firestore_read') &&
    scopes.includes('/') && count !== null && count > 0;
  const closeOnLink = (event: React.MouseEvent<HTMLElement>) => {
    if (event.target instanceof Element && event.target.closest('a')) onNavigate?.();
  };

  return (
    <>
      {/*
        NAV ORDER RULE — do not break:
        1. "Devices" is pinned first (primary work surface).
        2. A divider follows.
        3. EVERY other link stays in strict ALPHABETICAL order by its label text.
        When adding a new link, insert it alphabetically among the items below —
        do NOT append to the end. Plan 94 Phase 6: Analyze/Debug/Notes/Photos are
        tabs inside the device workspace; their old routes redirect there.
      */}
      <nav aria-label="Console" className="flex flex-col gap-0.5" onClick={closeOnLink}>
        {hasPermission('read_devices') && (
          <NavLink to="/devices" className={linkClass}>Devices</NavLink>
        )}

        <hr className="my-2 border-sidebar-border" />

        {hasPermission('read_alerts') && (
          <NavLink to="/alerts" className={linkClass}>Alerts</NavLink>
        )}
        {hasPermission('firestore_read') && (
          <NavLink to="/app-data" className={linkClass}>App Data</NavLink>
        )}
        {hasPermission('firestore_read') && (
          <NavLink
            to="/app-users"
            className={linkClass}
            aria-label={showUnreadCount
              ? `App Users, ${count} unread ${count === 1 ? 'note' : 'notes'}`
              : 'App Users'}
          >
            <span className="flex items-center justify-between gap-1">
              App Users
              {showUnreadCount && (
                <span
                  aria-hidden="true"
                  className="rounded-full border border-status-warning-border bg-status-warning px-1.5 text-xs font-semibold text-status-warning-foreground"
                >{count}</span>
              )}
            </span>
          </NavLink>
        )}
        {hasPermission('view_dashboards') && (
          <NavLink to="/dashboards" className={linkClass}>Dashboards</NavLink>
        )}
        {hasPermission('preview_data_cleanup') && (
          <NavLink to="/data-cleanup" className={linkClass}>Data Cleanup</NavLink>
        )}
        {hasPermission('read_devices') && (
          <NavLink to="/edit-device" className={linkClass}>Device List</NavLink>
        )}
        {hasPermission('enroll') && (
          <NavLink to="/enroll" className={linkClass}>Enroll</NavLink>
        )}
        {hasPermission('read_devices') && (
          <NavLink to="/equipment-catalog" className={linkClass}>Equipment Catalog</NavLink>
        )}
        {hasPermission('read_devices') && (
          <NavLink to="/equipment-inventory" className={linkClass}>Equipment Inventory</NavLink>
        )}
        {hasPermission('admin_users') && (
          <NavLink to="/features" className={linkClass}>Features</NavLink>
        )}
        {hasPermission('filters') && (
          <NavLink to="/filter-catalog" className={linkClass}>Filters</NavLink>
        )}
        {hasPermission('read_firmware') && (
          <NavLink to="/firmware" className={linkClass}>Firmware</NavLink>
        )}
        {hasPermission('read_health') && (
          <NavLink to="/fleet-health" className={linkClass}>Fleet Health</NavLink>
        )}
        {hasPermission('debug') && (
          <NavLink to="/read-bundles" className={linkClass}>Read Bundles</NavLink>
        )}
        {hasPermission('system_admin') && (
          <NavLink to="/system" className={linkClass}>System</NavLink>
        )}
        {hasPermission('read_devices') && (
          <NavLink to="/tags" className={linkClass}>Tags</NavLink>
        )}
        {hasPermission('admin_users') && (
          <NavLink to="/users" className={linkClass}>Users</NavLink>
        )}
      </nav>
      <div className="mt-auto border-t border-sidebar-border px-2 pt-4">
        {email && (
          <div
            className="mb-2 break-all text-xs text-sidebar-secondary-foreground"
            title={email}
          >
            {email}
          </div>
        )}
        <Button
          variant="danger"
          className="w-full"
          onClick={logout}
          title="Sign out of the console in this browser; you will need to log in again to come back"
        >
          Logout
        </Button>
      </div>
    </>
  );
};

// Below Tailwind's lg breakpoint (1024px) the sidebar is a one-line bar and
// the links open from a Menu button, so the page starts at the top of the
// window instead of below every link. From lg up it sticks to the top AND the
// left edge of the window, so a page that scrolls sideways leaves it in place,
// and it scrolls down only: overflow-y alone would let it scroll sideways too
// (FS-1162).
const Navbar: React.FC = () => {
  const { isLoggedIn } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);

  if (!isLoggedIn) return null;

  return (
    <aside aria-label="Console sidebar" className="flex w-full shrink-0 items-center justify-between border-b border-sidebar-border bg-sidebar p-3 text-sidebar-foreground lg:sticky lg:top-0 lg:left-0 lg:h-screen lg:w-36 lg:flex-col lg:items-stretch lg:justify-start lg:overflow-x-hidden lg:overflow-y-auto lg:border-r lg:border-b-0">
      <h2 className="px-3 font-heading text-xl font-semibold lg:mb-4 lg:pt-2">FluxHive</h2>
      <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
        <SheetTrigger
          render={<Button variant="secondary" className="lg:hidden" title="Open the console menu: every page, your account and Logout" />}
        >
          <Menu className="h-4 w-4" aria-hidden />
          Menu
        </SheetTrigger>
        <SheetContent
          side="left"
          showCloseButton={false}
          className="gap-0 overflow-y-auto bg-sidebar p-3 text-sidebar-foreground"
        >
          <div className="mb-4 flex items-center justify-between gap-2">
            <SheetTitle className="px-3 text-xl font-semibold text-sidebar-foreground">
              FluxHive <span className="sr-only">menu</span>
            </SheetTitle>
            <SheetClose render={<Button variant="secondary" title="Close the console menu and return to the page" />}>
              <X className="h-4 w-4" aria-hidden />
              Close
            </SheetClose>
          </div>
          <ConsoleNavigation onNavigate={() => setMenuOpen(false)} />
        </SheetContent>
      </Sheet>
      <div className="hidden lg:flex lg:flex-1 lg:flex-col">
        <ConsoleNavigation />
      </div>
    </aside>
  );
};

export default Navbar;
